import React, { useEffect, useState, useMemo, useCallback } from 'react';
import DevkitCard from './DevkitCard';
import SSHTerminal from './SSHTerminal';
import VNCProxy from './VNCProxy';
import SunshineView from './SunshineView';
// add these with your other imports
import ConnectionIcons from './ConnectionIcons';
import ConnectionCount from './ConnectionCount';
import appleIconWhite from '../assets/appleIconWhite.svg';

import { useUser } from '../contexts/UserContext';
import DeviceSearchBar, { matchesDeviceSearch } from './DeviceSearchBar';
import '../index.css';
import CopyButton from './CopyButton';
import { licenseDaysRemaining, licenseExpiryTone, formatLicenseDate, proteusExpiration, apolloExpiration } from '../utils/license';


const DevkitListView = ({ devkits, setDevices, onlyShowOnline, selectedCompany, selectedDeviceTypes = [], companyList, pinnedDevkits = [], onTogglePin, search = '', onSearchChange, connectionsByHost = {} }) => {
  const getDeviceType = (d) => {
    if (d.is_mac_mini) return 'macmini';
    return Number(d.devkit_id) >= 2000 ? 'apollocore' : 'proteuscore';
  };

  const matchesDeviceType = (d, selectedTypes) => {
    if (!selectedTypes || selectedTypes.length === 0) return true;
    return selectedTypes.includes(getDeviceType(d));
  };
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedTerminalOpen, setSelectedTerminalOpen] = useState(null);
  const [activeDevkitSSH, setActiveDevkitSSH] = useState(null);
  const [activeDevkitVNC, setActiveDevkitVNC] = useState(null);
  const [activeDevkitStream, setActiveDevkitStream] = useState(null);
  // const admin = localStorage.getItem('admin') === 'true'
  const { admin } = useUser();

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const openSSH = (e, id) => {
    e.stopPropagation();
    setSelectedTerminalOpen(id);
  };

  const closeSSH = () => {
    setSelectedTerminalOpen(null);
    setActiveDevkitSSH(null);
  };

  const openStream = (e, devkit) => {
    e.stopPropagation();
    setActiveDevkitStream(devkit);
  };

  const openPopup = (devkit) => {
    if (devkit.status === 'offline' && !devkit.is_mac_mini) return;
    setSelectedDevice(devkit);
  }

  const closePopup = () => setSelectedDevice(null);

  useEffect(() => {
    const selectedDevkit =
      devkits.find((d) => d.devkit_id === selectedTerminalOpen) || null;
    setActiveDevkitSSH(selectedDevkit);
  }, [selectedTerminalOpen, devkits]);

  const parseLastSeen = (text) => {
    if (!text || text.toLowerCase() === 'unknown') return Infinity;
    const [valueStr, unit] = text.split(' ');
    const value = parseInt(valueStr, 10);
    if (isNaN(value)) return Infinity;
    if (unit.startsWith('minute')) return value;
    if (unit.startsWith('hour')) return value * 60;
    if (unit.startsWith('day')) return value * 1440;
    return Infinity;
  };

  const parseVersionArray = (v) => {
    if (typeof v !== 'string') return null;
    if (!/^\d+(\.\d+)*$/.test(v)) return null;
    return v.split('.').map((n) => parseInt(n, 10));
  };

  const compareVersionArray = (a, b) => {
    for (let i = 0; i < 3; i++) {
      const ai = a[i] || 0;
      const bi = b[i] || 0;
      if (ai !== bi) return ai - bi;
    }
    return 0;
  };

  const compareVersionStrict = (v1 = '', v2 = '') => {
    const arr1 = parseVersionArray(v1);
    const arr2 = parseVersionArray(v2);

    // If both missing/invalid → equal
    if (arr1 === null && arr2 === null) return 0;
    // If v1 missing but v2 valid → v1 goes after v2
    if (arr1 === null && arr2 !== null) return 1;
    // If v2 missing but v1 valid → v1 goes before v2
    if (arr1 !== null && arr2 === null) return -1;
    // Both valid → numeric compare
    return compareVersionArray(arr1, arr2);
  };

  const sortedDevkits = useMemo(() => {
    if (!sortConfig.key) return devkits;

    const { key, direction } = sortConfig;
    return [...devkits].sort((a, b) => {
      let cmp = 0;

      switch (key) {
        case 'name': {
          const na = Number(a.devkit_id);
          const nb = Number(b.devkit_id);
          cmp = Number.isNaN(na) || Number.isNaN(nb)
            ? String(a.devkit_id).localeCompare(String(b.devkit_id))
            : na - nb;
          break;
        }

        case 'company':
          cmp = (a.company || '').localeCompare(b.company || '');
          break;

        case 'status':
          cmp =
            (a.status === 'online' ? 0 : 1) -
            (b.status === 'online' ? 0 : 1);
          break;


        case 'lastSeen': {
          const la = parseLastSeen(a.lastSeen);
          const lb = parseLastSeen(b.lastSeen);
          if (la === lb) cmp = 0;
          else if (la === Infinity) cmp = 1;
          else if (lb === Infinity) cmp = -1;
          else cmp = la - lb;
          break;
        }

        case 'imageVersion': {
          const va = a.versions?.poseidon?.devkit_image_version || a.last_image_version || '';
          const vb = b.versions?.poseidon?.devkit_image_version || b.last_image_version || '';
          cmp = compareVersionStrict(va, vb);
          break;
        }

        case 'apollocore': {
          const va = a.versions?.apollocore?.apollocore_version || a.last_apollocore_version || '';
          const vb = b.versions?.apollocore?.apollocore_version || b.last_apollocore_version || '';
          cmp = compareVersionStrict(va, vb);
          break;
        }

        case 'proteuscore': {
          const va = a.versions?.proteuscore?.proteuscore_version || a.last_proteuscore_version || '';
          const vb = b.versions?.proteuscore?.proteuscore_version || b.last_proteuscore_version || '';
          cmp = compareVersionStrict(va, vb);
          break;
        }

        case 'ironman': {
          const va = a.versions?.ironman?.ironman_version || a.last_ironman_version || '';
          const vb = b.versions?.ironman?.ironman_version || b.last_ironman_version || '';
          cmp = compareVersionStrict(va, vb);
          break;
        }

        case 'poseidon': {
          const va = a.versions?.poseidon?.poseidon_version || a.last_poseidon_version || '';
          const vb = b.versions?.poseidon?.poseidon_version || b.last_poseidon_version || '';
          cmp = compareVersionStrict(va, vb);
          break;
        }

        case 'license': {
          // Sort by the soonest expiry across ProteusCore/ApolloCore; missing goes last.
          const soonest = (d) => {
            const days = [proteusExpiration(d), apolloExpiration(d)]
              .map(licenseDaysRemaining)
              .filter((n) => n !== null);
            return days.length ? Math.min(...days) : Infinity;
          };
          const la = soonest(a);
          const lb = soonest(b);
          if (la === lb) cmp = 0;
          else if (la === Infinity) cmp = 1;
          else if (lb === Infinity) cmp = -1;
          else cmp = la - lb;
          break;
        }

        default:
          cmp = 0;
      }

      return direction === 'asc' ? cmp : -cmp;
    });
  }, [devkits, sortConfig]);


  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const SortArrow = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const openVncSelector = (e, devkit) => {
    e.stopPropagation();
    setActiveDevkitVNC({
      devkitId: devkit.devkit_id,
      host: devkit.tailscale.ipv4[0],
      vncUsername: devkit.vnc_username ?? null,
      vncPassword: devkit.vnc_password ?? null,
      macMini: devkit.is_mac_mini ?? false,
    });
  };

  const closeVnc = useCallback(() => setActiveDevkitVNC(null), []);

  // The active VNC/stream state is a snapshot taken at click time — live
  // online/offline comes from the polled `devkits` prop, so open sessions can
  // overlay "device went offline" instead of freezing silently.
  const isDevkitOnline = useCallback(
    (devkitId) => {
      const d = devkits.find((dk) => dk.devkit_id === devkitId);
      return d ? d.status !== 'offline' : false;
    },
    [devkits]
  );

  const filtered = sortedDevkits
    .filter((d) => !onlyShowOnline || d.status === 'online')
    .filter((d) => selectedCompany === null || d.company === selectedCompany)
    .filter((d) => matchesDeviceType(d, selectedDeviceTypes))
    .filter((d) => matchesDeviceSearch(d, search));

  const headerCell = (label, sortKey, extraClass = '') => (
    <th
      className={`px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider select-none ${sortKey ? 'cursor-pointer hover:text-gray-700' : ''} ${extraClass}`}
      onClick={sortKey ? () => requestSort(sortKey) : undefined}
    >
      {label}
      {sortKey && <SortArrow columnKey={sortKey} />}
    </th>
  );

  return (
    <>
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <DeviceSearchBar
            value={search}
            onChange={onSearchChange}
            matchCount={filtered.length}
            totalCount={devkits.length}
          />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-50">
              <tr>
                {headerCell('Device', 'name')}
                {headerCell('Company', 'company')}
                {headerCell('Status', 'status')}
                {headerCell('DevKit Image', 'imageVersion')}
                {headerCell('Last Seen', 'lastSeen')}
                {headerCell('ApolloCore Version', 'apollocore')}
                {headerCell('ProteusCore Version', 'proteuscore')}
                {headerCell('Config App Version', 'ironman')}
                {headerCell('Poseidon Version', 'poseidon')}
                {headerCell('License Expiry', 'license')}
                {headerCell('Remote Access', null)}
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200 [&>tr>td]:align-middle">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-6 py-16">
                    <div className="flex flex-col items-center justify-center text-center">
                      <h3 className="mt-3 text-lg font-semibold text-gray-900">
                        No matching DevKits
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {search
                          ? `Nothing matches “${search}”.`
                          : onlyShowOnline || selectedCompany || selectedDeviceTypes.length > 0
                            ? "Try adjusting your filters or showing offline devices."
                            : "There aren’t any DevKits to display right now."}
                      </p>
                      <svg className="h-8 w-8 text-gray-400" viewBox="0 0 24 24" fill="none">
                        <path d="M4 7h16M4 12h10M4 17h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((devkit) => (
                  <tr
                    key={devkit.tailscale.id}
                    className={`transition-colors duration-150 ${devkit.status === 'online' || devkit.is_mac_mini
                      ? 'hover:bg-blue-50 cursor-pointer'
                      : 'opacity-60'
                      }`}
                    onClick={() => openPopup(devkit)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onTogglePin?.(devkit.devkit_id);
                          }}
                          title={pinnedDevkits.includes(devkit.devkit_id) ? 'Unpin' : 'Pin to top'}
                          className={`text-base leading-none transition-colors ${pinnedDevkits.includes(devkit.devkit_id)
                            ? 'text-blue-600 hover:text-blue-700'
                            : 'text-gray-300 hover:text-blue-500'
                            }`}
                        >
                          ★
                        </button>
                        <div className="flex flex-col">
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                            {devkit.devkit_id}
                            {devkit.is_mac_mini && (
                              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Mac Mini</span>
                            )}
                            {devkit.is_test_vessel && (
                              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">R&D</span>
                            )}
                          </div>
                          {admin && (
                            <div className="text-xs text-gray-500 font-mono flex items-center gap-1">
                              {devkit.tailscale.ipv4[0]}
                              <CopyButton text={devkit.tailscale.ipv4[0]} label="Copy Tailscale IP" size={12} />
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            {devkit.tailscale.hostname}.local
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex flex-col items-center w-full">
                        <span>{devkit.company}</span>
                        {devkit.vessel_name && (
                          <span className="italic text-gray-400 text-xs">{devkit.vessel_name}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-col items-center gap-1">
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded-full ${devkit.status === 'online'
                            ? 'text-green-900 subtle-green-pulse'
                            : 'bg-red-100 text-red-800'
                            }`}
                        >
                          {devkit.status[0].toUpperCase() + devkit.status.slice(1)}
                        </span>
                        <ConnectionIcons versions={devkit?.versions} size="sm" className="justify-center" />
                        <ConnectionCount connections={connectionsByHost[devkit.tailscale?.ipv4?.[0]]} />
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                      {devkit?.versions?.poseidon?.devkit_image_version && devkit.versions.poseidon.devkit_image_version !== 'Unknown'
                        ? `v${devkit.versions.poseidon.devkit_image_version}`
                        : devkit?.last_image_version && devkit?.last_image_version !== 'Unknown'
                          ? `v${devkit.last_image_version}`
                          : 'Unknown'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                      {devkit.lastSeen}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                      {Number(devkit.devkit_id) >= 2000 ? (
                        <span className="text-sm bg-gray-100 px-4 py-1 rounded">
                          {
                            devkit?.versions?.apollocore?.apollocore_version ??
                            devkit?.last_apollocore_version ??
                            'unknown'
                          }
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                      <span className="text-sm bg-gray-100 px-4 py-1 rounded">
                        {
                          devkit?.versions?.proteuscore?.proteuscore_version ??
                          devkit?.last_proteuscore_version ??
                          'unknown'
                        }
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                      <span className="text-sm bg-gray-100 px-4 py-1 rounded">
                        {
                          devkit?.versions?.ironman?.ironman_version ??
                          devkit?.last_ironman_version ??
                          'unknown'
                        }
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-center">
                      <span className="text-sm bg-gray-100 px-4 py-1 rounded">
                        {
                          devkit?.versions?.poseidon?.poseidon_version
                          ?? devkit?.last_poseidon_version
                          ?? 'unknown'
                        }
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-center">
                      {devkit.is_mac_mini ? (
                        <span className="text-sm text-gray-400">—</span>
                      ) : (
                        <div className="flex flex-col gap-0.5 items-center">
                          <LicenseExpiryLine label="Proteus" date={proteusExpiration(devkit)} />
                          {Number(devkit.devkit_id) >= 2000 && (
                            <LicenseExpiryLine label="Apollo" date={apolloExpiration(devkit)} />
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-col gap-1 items-center w-full">
                        {/* Devkit SSH + VNC + Stream, stacked to save width */}
                        <div className="flex flex-col gap-1 w-full max-w-24">
                          {admin && (
                            <button
                              onClick={(e) => openSSH(e, devkit.devkit_id)}
                              className="flex items-center justify-center gap-1 w-full px-2.5 py-1 text-xs whitespace-nowrap bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-600"
                              title={devkit.status === 'offline' ? 'Device is offline' : 'SSH Connect'}
                              disabled={devkit.status === 'offline'}
                            >
                              {devkit.is_mac_mini && <img src={appleIconWhite} alt="Apple" className="w-3 h-3" />}
                              SSH
                            </button>
                          )}
                          <button
                            onClick={(e) => openVncSelector(e, devkit)}
                            className="flex items-center justify-center gap-1 w-full px-2.5 py-1 text-xs whitespace-nowrap bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-600"
                            title={devkit.status === 'offline' ? 'Device is offline' : 'VNC Connect'}
                            disabled={devkit.status === 'offline'}
                          >
                            {devkit.is_mac_mini && <img src={appleIconWhite} alt="Apple" className="w-3 h-3" />}
                            VNC
                          </button>
                          {devkit.sunshine_installed === true && (
                            <button
                              onClick={(e) => openStream(e, devkit)}
                              className="flex items-center justify-center gap-1 w-full px-2.5 py-1 text-xs whitespace-nowrap bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-600"
                              title={devkit.status === 'offline' ? 'Device is offline' : 'Moonlight Stream (Sunshine)'}
                              disabled={devkit.status === 'offline'}
                            >
                              {devkit.is_mac_mini && <img src={appleIconWhite} alt="Apple" className="w-3 h-3" />}
                              Stream
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>

          </table>
        </div>
      </div>

      {selectedDevice && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={closePopup}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-lg w-full px-4"
          >
            {(() => {
              const liveDevkit =
                devkits.find((d) => d.devkit_id === selectedDevice.devkit_id) ??
                selectedDevice;
              return (
                <DevkitCard
                  devkit={liveDevkit}
                  setDevices={setDevices}
                  companyList={companyList}
                  isPinned={pinnedDevkits.includes(selectedDevice.devkit_id)}
                  onTogglePin={onTogglePin}
                  connections={connectionsByHost[liveDevkit.tailscale?.ipv4?.[0]]}
                />
              );
            })()}
          </div>
        </div>
      )}

      {activeDevkitSSH && (
        <SSHTerminal
          host={activeDevkitSSH.tailscale.ipv4[0]}
          onClose={closeSSH}
          imageVersion={activeDevkitSSH.last_image_version}
          macMini={activeDevkitSSH.is_mac_mini ?? false}
          sshUsername={activeDevkitSSH.vnc_username ?? undefined}
          sshPassword={activeDevkitSSH.vnc_password ?? undefined}
        />
      )}

      {activeDevkitVNC && (
        <VNCProxy
          host={activeDevkitVNC.host}
          port={5900}
          macMini={activeDevkitVNC.macMini}
          vncUsername={activeDevkitVNC.vncUsername}
          vncPassword={activeDevkitVNC.vncPassword}
          onClose={closeVnc}
          deviceOnline={isDevkitOnline(activeDevkitVNC.devkitId)}
          deviceName={`Devkit ${activeDevkitVNC.devkitId}`}
        />
      )}

      {activeDevkitStream && (
        <SunshineView
          devkit={activeDevkitStream}
          admin={admin}
          onClose={() => setActiveDevkitStream(null)}
          deviceOnline={isDevkitOnline(activeDevkitStream.devkit_id)}
        />
      )}

    </>
  );
};

// Just the expiration date, coloured by urgency; core name and days remaining live in the tooltip.
const LicenseExpiryLine = ({ label, date }) => {
  const days = licenseDaysRemaining(date);
  const tone = licenseExpiryTone(days);
  const detail =
    days === null ? '' : days < 0 ? ' (expired)' : ` (${days} day${days === 1 ? '' : 's'} remaining)`;
  return (
    <span className={tone} title={`${label}Core license expiration${detail}`}>
      {formatLicenseDate(date)}
    </span>
  );
};

export default DevkitListView;
